// tag-health.ts — pure functions that turn a (classification, summary) pair
// into a human-readable health verdict. The same `Finding[]` is consumed by
// the "Problems" filter (severity = bucket selection), the row health chip
// (worst severity), and the detail panel's Overview tab (rendered verbatim
// as bullets).
//
// Inputs are independent and optional: a classified tag with no stats still
// gets a partial verdict; an unclassified tag with stats can still surface
// generic warnings (z-score outliers, staleness). Tests in tag-health.spec.ts
// pin every branch.

import type {
  TagClassificationRecord,
  TagNumericSummary,
  TagRecord,
  TagStateSummary,
  TagSummaryRecord
} from '@axilon-apps/domains-ontology-ui-state'
import { isCollectingHistory } from './historization-status'
import { makeBucketFor, type StateBucket } from './state-buckets'

/**
 * Lookback behind the list/inventory trend pills. The UI sends no window, so
 * the proxy default (`defaultWindow` in proxy-server/internal/ontology/tag/stats)
 * applies. Keep this label in step with it. The detail panel has its own
 * explicit 24h window and does not use this.
 */
export const INVENTORY_TREND_WINDOW_LABEL = '1h'
export const INVENTORY_TREND_WINDOW_PHRASE = '1-hour'

export type HealthSeverity = 'bad' | 'warn' | 'ok'

export type HealthStatus = HealthSeverity | 'unknown'

export interface HealthFinding {
  /** Worst-to-best severity of this individual finding. */
  severity: HealthSeverity
  /** Stable identifier — easier to filter/group on than the message. */
  code:
    | 'state_bad'
    | 'state_warning'
    | 'accumulator_stalled'
    | 'value_below_range'
    | 'value_above_range'
    | 'value_outside_percentage'
    | 'value_outlier_z'
    | 'data_stale'
    | 'no_recent_readings'
    | 'bad_quality'
  /** Plain-language sentence rendered verbatim in the Overview tab. */
  message: string
}

export interface HealthVerdict {
  status: HealthStatus
  findings: HealthFinding[]
  /** True when there's not enough data to say anything useful at all. */
  hasData: boolean
}

// Maximum |z-score| tolerated before the last reading is called an outlier.
// 3σ is a generic two-tailed "rare event" threshold; the bullet is only
// emitted for unclassified numeric tags where we have no role-specific band.
const Z_OUTLIER_THRESHOLD = 3

// Stale-readings threshold (multiplier on the data-quality mean gap). The
// data-quality block computes mean and longest gap over the window; we call
// a tag "stale" when its current staleness is well above the typical gap
// (so a slow-but-healthy 1/min sensor doesn't constantly flag).
const STALE_GAP_MULTIPLIER = 5
// Hard floor to keep extremely fast tags (every-second readings) from
// reporting "stale" the moment one sample is dropped.
const STALE_FLOOR_SECONDS = 120

// Worst-severity reducer — used to fold findings into the row chip status.
const SEVERITY_ORDER: Record<HealthSeverity, number> = { bad: 2, warn: 1, ok: 0 }

function worse(a: HealthSeverity | null, b: HealthSeverity): HealthSeverity {
  if (a === null) return b
  return SEVERITY_ORDER[a] >= SEVERITY_ORDER[b] ? a : b
}

function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '?'
  if (n === 0) return '0'
  const abs = Math.abs(n)
  const digits = abs >= 100 ? 1 : abs >= 1 ? 2 : 4
  return n.toLocaleString(undefined, { maximumFractionDigits: digits })
}

function fmtDuration(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '?'
  if (secs < 60) return `${Math.round(secs)}s`
  const m = secs / 60
  if (m < 60) return `${Math.round(m)}m`
  const h = m / 60
  if (h < 24) return `${h.toFixed(h < 10 ? 1 : 0)}h`
  return `${(h / 24).toFixed(1)}d`
}

function bucketForState(
  classification: TagClassificationRecord,
  state: string
): StateBucket | null {
  return makeBucketFor(classification)?.(state) ?? null
}

// --- per-branch evaluators -------------------------------------------------

function evaluateState(
  classification: TagClassificationRecord | undefined,
  state: TagStateSummary
): HealthFinding[] {
  const findings: HealthFinding[] = []
  const current = state.currentState
  if (current === null) return findings
  if (classification) {
    const bucket = bucketForState(classification, current)
    if (bucket === 'bad') {
      const duration = state.currentDurationSeconds
      const durationText = duration != null && duration >= 0 ? ` for ${fmtDuration(duration)}` : ''
      findings.push({
        severity: 'bad',
        code: 'state_bad',
        message: `In known bad state "${current}"${durationText}.`
      })
    } else if (bucket === 'warning') {
      findings.push({
        severity: 'warn',
        code: 'state_warning',
        message: `In warning state "${current}".`
      })
    }
    // bucket === null → the state is unknown: never surfaced as a problem
    // (unknown renders the same as good in the UI).
  }
  return findings
}

function evaluateNumeric(
  classification: TagClassificationRecord | undefined,
  numeric: TagNumericSummary
): HealthFinding[] {
  const findings: HealthFinding[] = []
  const last = numeric.lastValue
  const lo = classification?.expectedRangeLow ?? null
  const hi = classification?.expectedRangeHigh ?? null
  const role = classification?.role ?? null

  // Role-aware bullets (classified branch).
  if (classification) {
    if (role === 'accumulator' && numeric.max !== null && numeric.min !== null) {
      const delta = numeric.max - numeric.min
      if (delta <= 0) {
        findings.push({
          severity: 'bad',
          code: 'accumulator_stalled',
          message: 'Accumulator stopped increasing over the stats window.'
        })
      }
    }
    if (last !== null && lo !== null && hi !== null) {
      if (last < lo) {
        findings.push({
          severity: 'bad',
          code: 'value_below_range',
          message: `Below expected range — ${fmtNum(last)} is ${fmtNum(lo - last)} under ${fmtNum(lo)}.`
        })
      } else if (last > hi) {
        findings.push({
          severity: 'bad',
          code: 'value_above_range',
          message: `Above expected range — ${fmtNum(last)} is ${fmtNum(last - hi)} over ${fmtNum(hi)}.`
        })
      }
    }
    if (
      classification.isPercentage === true &&
      lo === null &&
      hi === null &&
      last !== null &&
      (last < 0 || last > 100)
    ) {
      findings.push({
        severity: 'bad',
        code: 'value_outside_percentage',
        message: `Out of 0–100% bounds (${fmtNum(last)}).`
      })
    }
  }

  // Class-agnostic outlier check (always run when we have a z-score, but
  // suppressed for sensors that already triggered a range bullet — the
  // range bullet is the more specific finding and avoids double-reporting).
  // Also suppressed for setpoints: operators change setpoints routinely, so
  // movement (which is exactly what a z-outlier on a setpoint means — the
  // last value sits far from the window mean) is normal operation, never a
  // health signal.
  const alreadyOutOfRange = findings.some(
    (f) => f.code === 'value_below_range' || f.code === 'value_above_range'
  )
  if (
    !alreadyOutOfRange &&
    role !== 'setpoint' &&
    numeric.lastZScore !== null &&
    Math.abs(numeric.lastZScore) >= Z_OUTLIER_THRESHOLD
  ) {
    findings.push({
      severity: 'warn',
      code: 'value_outlier_z',
      message: `Outside the typical range — ${Math.abs(numeric.lastZScore).toFixed(1)}σ from the window mean.`
    })
  }

  return findings
}

function evaluateDataQuality(
  summary: TagSummaryRecord,
  expectedGapSeconds: number
): HealthFinding[] {
  const dq = summary.dataQuality
  // A newest reading that is bad is the strongest statement available: the
  // source answered and reported a fault. Checked before the empty-window
  // case, because change-only ingest records that fault once and then goes
  // quiet, draining the window while the fault is still current.
  if (dq.lastReadingIsGood === false) {
    return [
      {
        severity: 'bad',
        code: 'bad_quality',
        message:
          dq.sampleCount > 0 && dq.goodCount === 0
            ? `No usable readings: all ${dq.sampleCount} in the stats window have bad quality.`
            : 'The most recent reading has bad quality.'
      }
    ]
  }
  if (dq.sampleCount === 0) {
    return [
      {
        severity: 'warn',
        code: 'no_recent_readings',
        message: 'No readings in the stats window.'
      }
    ]
  }
  // Readings exist and not one of them is usable. Worse than staleness, and
  // without this the tag scores ok: the quality counts were displayed all
  // along but never reached the verdict.
  if (dq.goodCount === 0) {
    return [
      {
        severity: 'bad',
        code: 'bad_quality',
        message: `No usable readings: all ${dq.sampleCount} in the stats window have bad quality.`
      }
    ]
  }
  const staleness = dq.stalenessSeconds
  if (staleness === null) return []
  const meanGap = dq.meanGapSeconds ?? 0
  // Per-source cadence floor: a slow source (e.g. a 15-min DB sample) must
  // not read as "stale" at the 120s tag floor before enough readings exist
  // to make meanGap reflect the true cadence. Take the larger of the
  // observed gap and the registered cadence, then apply the same multiplier.
  const effectiveGap = Math.max(meanGap, expectedGapSeconds)
  const threshold = Math.max(STALE_FLOOR_SECONDS, effectiveGap * STALE_GAP_MULTIPLIER)
  if (staleness > threshold) {
    return [
      {
        severity: 'warn',
        code: 'data_stale',
        message: `No fresh readings for ${fmtDuration(staleness)} (typical gap ${fmtDuration(meanGap)}).`
      }
    ]
  }
  return []
}

/**
 * Whether a trend may be computed for this tag. Trends summarize readings
 * collected into the internal historian, so a graph-only, disabled or
 * quarantined tag has no current readings to evaluate; anything still in the
 * store predates the change, and external (PI / Ignition) history is not
 * what the trend evaluator reads.
 */
export function isTagTrendEligible(
  tag: Pick<TagRecord, 'state'> & { graphOnly?: boolean; acquisitionQuarantined?: boolean }
): boolean {
  return isCollectingHistory({
    graphOnly: tag.graphOnly,
    enabled: tag.state !== 'disabled',
    acquisitionQuarantined: tag.acquisitionQuarantined
  })
}

/** Neutral verdict for a tag with no trend: nothing is evaluated. */
export const NO_TREND_VERDICT: Readonly<HealthVerdict> = {
  status: 'unknown',
  findings: [],
  hasData: false
}

/** Options that tune the verdict for a specific tag's acquisition posture. */
export interface EvaluateTagHealthOptions {
  /**
   * Registered scrape cadence in milliseconds. Feeds the per-source
   * staleness floor so slow sources (a 15-min DB sample) aren't flagged
   * stale at the 120s tag floor. 0/undefined → floor is the observed gap
   * only (the historical behavior for fast poll tags).
   */
  frequencyMs?: number
}

/**
 * Evaluate a tag's health from its classification and stats summary. Either
 * input may be undefined — the function degrades gracefully:
 *   - missing summary → unknown verdict
 *   - missing classification + numeric summary → class-agnostic findings only
 *   - missing classification + state summary → no findings (we don't know
 *     which states are bad)
 *
 * `options.frequencyMs` widens the staleness floor for slow sources; keep
 * this logic in lock-step with the Go rollup evaluator (health_eval.go).
 */
export function evaluateTagHealth(
  classification: TagClassificationRecord | undefined,
  summary: TagSummaryRecord | undefined,
  options: EvaluateTagHealthOptions = {}
): HealthVerdict {
  if (!summary) {
    return { status: 'unknown', findings: [], hasData: false }
  }
  const expectedGapSeconds =
    options.frequencyMs && options.frequencyMs > 0 ? options.frequencyMs / 1000 : 0
  const findings: HealthFinding[] = []
  if (summary.state) {
    findings.push(...evaluateState(classification, summary.state))
  }
  if (summary.numeric) {
    findings.push(...evaluateNumeric(classification, summary.numeric))
  }
  findings.push(...evaluateDataQuality(summary, expectedGapSeconds))

  // hasData is false when we literally have nothing to assess — no readings
  // and no classification. The Overview tab uses this to fall back to a
  // "Not enough data" verdict instead of "Looks normal".
  //
  // A recorded last-row quality also counts. Behind change-only ingest a
  // faulted tag can hold zero samples in the window and still be known to be
  // faulted, and "not enough data" is the wrong answer for a tag we just
  // heard report a fault.
  const hasData =
    summary.dataQuality.sampleCount > 0 ||
    summary.dataQuality.lastReadingIsGood !== null ||
    summary.numeric !== null ||
    summary.state !== null
  if (!hasData) {
    return { status: 'unknown', findings, hasData }
  }

  let worst: HealthSeverity | null = null
  for (const f of findings) worst = worse(worst, f.severity)
  const status: HealthStatus = worst ?? 'ok'
  return { status, findings, hasData }
}
